<template>
  <div>
    <!-- Icon to be displayed as long as the dialog is closed -->
    <v-btn :color="color" icon :small="xs" @click="$store.commit('search/setIsSearchVisible', true)">
      <v-icon>mdi-magnify</v-icon>
    </v-btn>

    <!-- Search Dialog -->
    <v-dialog
      v-model="isSearchVisible"
      :fullscreen="xs || sm"
      :flat="xs"
      scrollable
      :overlay-opacity="0.95"
      :overlay-color="darkMode ? 'background lighten-1' : 'background darken-4'"
      maxWidth="1050"
      style="z-index: 20000"
    >
      <!-- inner container - required for dialog -->
      <div class="d-flex flex-column background" :style="`height:${mdAndUp ? '90vh' : '100%'}`">
        <!-- Title -->
        <div
          style="height: 60px; padding: 30px 10px !important"
          class="border-red d-flex align-center px-4 py-3 text-subtitle-1"
          :class="`${theme.appBar.other.bg} ${theme.appBar.other.color}--text text-no-wrap text-truncate ${xs ? 'small-title' : ''}`"
        >
          {{ isInFulltextSearchMode ? $t('search.title.fulltextSearchMode') : $t('search.title') }}
          <v-spacer></v-spacer>
          <v-btn icon :color="theme.appBar.other.color" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <!-- Search mask -->
        <div class="d-flex flex-column">
          <!-- Search form area -->
          <!-- Remark: class outlined-[theme] does not  apply border, so use inline style-->
          <div class="search-area pa-2">
            <!-- Search field, button and collapse/expand on xs for module selection-->
            <div class="full-width d-flex flex-wrap flex-row align-center">
              <div class="d-flex full-width align-center justify-end flex-wrap">
                <v-checkbox
                  hide-details
                  class="d-flex pl-3 pr-4 py-1 my-0 justify-center align-center align-end"
                  v-model="titleOnly"
                  :label="$t('search.option.titleOnly')"
                ></v-checkbox>
                <search-field :isInFulltextSearchMode="isInFulltextSearchMode" @onKeyDown="onKeyDown" class="pb-2"></search-field>
              </div>

              <!-- further options -->
              <div v-if="isFullTextSearchWorking && mdAndUp" class="d-flex flex-column px-4">
                <div class="d-flex flex-row align-start">
                  <v-radio-group
                    :label="$t('search.option.maxPagesPerDoc')"
                    v-model="maxPagesPerHit"
                    dense
                    column
                    class="my-0 py-0"
                    @change="searchAsYouTypeCurrentTerm()"
                  >
                    <v-radio :label="$t('search.option.maxPagesPerHit2')" :value="2"></v-radio>
                    <!-- <v-radio :label="$t('search.option.maxPagesPerHit10')" :value="10"></v-radio> -->
                    <v-radio :label="$t('search.option.maxPagesPerHitUndefined')" :value="1000"></v-radio>
                  </v-radio-group>

                  <v-radio-group
                    small
                    :label="$t('search.option.maxHighlightsPerPage')"
                    v-model="maxHighlightsPerPage"
                    dense
                    column
                    class="ml-7 my-0 py-0"
                    @change="searchAsYouTypeCurrentTerm()"
                  >
                    <v-radio :label="$t('search.option.maxHighlightsPerPage2')" :value="2"></v-radio>
                    <v-radio :label="$t('search.option.maxHighlightsPerPageUndefined')" :value="1000"></v-radio>
                  </v-radio-group>
                </div>
              </div>

              <checkbox-chip
                class="d-flex mb-1"
                v-if="isFullTextSearchWorking && isInFulltextSearchMode"
                :trigger="andMode"
                textKey="search.option.andMode"
                @click="andMode = !andMode"
              />
              <v-btn
                v-if="isFullTextSearchWorking && !mdAndUp"
                x-small
                icon
                class="d-flex ml-2"
                @click="showSearchSettings = !showSearchSettings"
                :color="theme.elements.color"
              >
                <rotate-icon :color="darkMode ? 'white' : 'gray'" icon="mdi-chevron-down" :trigger="showSearchSettings"></rotate-icon>
              </v-btn>
            </div>
            <div v-if="mdAndUp" class="d-flex flex-grow-1">
              <module-selection :listOfModules="listOfModules"></module-selection>
            </div>
          </div>
        </div>
        <!-- Search results -->
        <div class="d-flex flex-column flex-grow-1 scroll-container">
          <!-- Search Results -->
          <div class="px-0 mx-0 pt-1 d-flex column no-wrap" ref="scrollContainer">
            <virtual-list
              class="virtual-scroll px-0 px-sm-1 full-width"
              data-key="id"
              :data-sources="hits"
              :data-component="itemComponent"
              :keeps="virtualScrollKeepOption"
            />
          </div>
        </div>
        <!-- footer -->
        <div class="d-flex full-width">
          <search-dialog-bottom-bar
            @onClose="closeDialog()"
            :lastSearchWasAllSearch="lastSearchWasAllSearch"
            :loadedHits="hits.length"
            :totalHits="searchResults.length"
          ></search-dialog-bottom-bar>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import SearchItem from './SearchItem';
import VirtualList from 'vue-virtual-scroll-list';
import RotateIcon from '../icons/RotateIcon.vue';
import SearchDialogBottomBar from './SearchDialogBottomBar.vue';
import SearchField from './SearchField.vue';
import ModuleSelection from './ModuleSelection.vue';
import CheckboxChip from '@/components/search/CheckboxChip.vue';

export default {
  name: 'Search',
  mixins: [common, configMixin],
  components: {
    CheckboxChip,
    VirtualList,
    RotateIcon,
    SearchDialogBottomBar,
    SearchField,
    ModuleSelection,
  },
  props: {
    color: { type: String },
  },

  data() {
    return {
      itemComponent: SearchItem,
      showSearchSettings: false,
      limit: 300,

      fuzzy: false,
      andMode: true,
      showHighlightsWhileSearchAsYouType: true,
      maxPagesPerHit: 2,
      maxHighlightsPerPage: 2,
      lastSearchWasAllSearch: false, //true when the last search was executed with searchAllHits()
      isFullTextSearchWorking: false, // as of December 23 there is an issue with the fulltext search. As this feature seams to be rarely used, MS disabled it for the time being
    };
  },
  computed: {
    ...mapState({
      searchFieldTheme: (state) => state.theme.appliedTheme.components.searchField,

      hits: (state) => state.search.searchResults,
      // isSearching: (state) => state.search.isSearching,
      // selectedModules: (state) => state.search.selectedModules,
      isConnected: (state) => state.isConnected,
    }),
    ...mapState('search', ['searchResults', 'isSearching', 'selectedModules', 'virtualScrollKeepOption']),
    isInFulltextSearchMode() {
      return this.isConnected && this.features.fulltextSearchEnabled;
    },

    isSearchVisible: {
      get() {
        return this.$store.state.search.isSearchVisible;
      },
      set(value) {
        this.$store.commit('search/setIsSearchVisible', false);
      },
    },
    isSearching: {
      get() {
        return this.$store.state.search.isSearching;
      },
      set(value) {
        this.$store.commit('search/setIsSearching', value);
      },
    },
    search: {
      // getter
      get() {
        return this.$store.state.search.search;
      },
      // setter
      set(value) {
        this.$store.commit('search/setSearch', value);
      },
    },
    titleOnly: {
      // getter
      get() {
        return this.$store.state.search.titleOnly;
      },
      // setter
      set(value) {
        this.$store.commit('search/setTitleOnly', value);
      },
    },

    listOfModules() {
      return this.modules.map((m) => {
        return {
          value: { instance: m.instance, module: m.type },
          text: this.$te(m.title) ? this.$t(m.title) : m.title,
        };
      });
    },
  },
  methods: {
    closeDialog() {
      this.$store.commit('search/setIsSearchVisible', false);
    },

    onKeyDown(event) {
      if (event.key === 'Enter') {
        this.searchAllHits();
      }
    },
    updateSearch() {
      if (this.lastSearchWasAllSearch) {
        this.searchAllHits();
      } else {
        this.searchAsYouTypeCurrentTerm(50, this.showHighlightsWhileSearchAsYouType, false);
      }
    },
    /**
     * Triggers a search which delivers all hits
     */
    searchAllHits() {
      if (this.search !== '') {
        this.lastSearchWasAllSearch = true;
        this.searchAsYouTypeCurrentTerm(50, true, true); //this.limit
        //this.showSearchSettings = false;
      }
    },
    searchAsYouTypeCurrentTerm(limit = 50, showHighlights = true, forceSearch = false) {
      let searchTerms = this.andMode ? this.search.split(' ').join(' AND ') : this.search;

      //default instance 1 for modules having no instance (but index contains _1 in this case)
      let moduleInstances = this.selectedModules.map((m) => (m.value.instance ? `${m.value.module}_${m.value.instance}` : `${m.value.module}_1`));
      this.$store.dispatch('search/searchAllModules', {
        search: searchTerms,
        limit,
        maxPagesPerHit: this.maxPagesPerHit,
        maxHighlightsPerPage: this.maxHighlightsPerPage,
        forceSearch,

        moduleInstances,
        showHighlights: showHighlights,
        titleOnly: this.titleOnly,
        fuzzy: this.fuzzy,

        wrapWithWildcards: false, //if set to true each search text is wrapped with
        //module: this.selectedModules.map((m) => m.value.module.toLowerCase()),
        isActive: true,
        isArchived: false,
        isDeleted: false,
      });
    },
    searchAsYouType(searchWordsTo, searchWordsFrom) {
      if (searchWordsTo && searchWordsTo !== searchWordsFrom && searchWordsTo !== '' && searchWordsTo.length >= 2) {
        this.lastSearchWasAllSearch = true; //false;
        this.searchAsYouTypeCurrentTerm(2, this.showHighlightsWhileSearchAsYouType, false);
      } else if (searchWordsTo === '') {
        this.$store.commit('search/resetSearchResults', { root: true });
      }
    },
  },
  watch: {
    hits() {
      // Make sure, the scrollcontainer is visible from its top
      if (this.$refs.scrollContainer) this.$refs.scrollContainer.scrollIntoView();
    },

    search(to, from) {
      // trigger a search but only in the cas no search is pending
      if (this.isSearching === false) {
        this.searchAsYouType(to, from);
        // utils.debounce((to, from) => this.searchAsYouType(to, from), 1000);
      }
    },
    isSearchVisible(to, from) {
      if (!from && to) {
        if (this.$route.meta.moduleType) {
          const currentModule = [
            {
              text: this.mx_moduleTitle,
              value: {
                instance: this.$route.params.instance ? parseInt(this.$route.params.instance) : undefined,
                module: this.$route.meta.moduleType,
              },
            },
          ];
          this.$store.commit('search/setSelectedModules', currentModule);
        } else {
          this.$store.commit('search/setSelectedModules', []);
        }
      }
      if (to === true) {
        this.searchAsYouType(this.search, '');
      } else {
        this.$store.commit('search/setSearch', '');
      }
    },
    selectedModules() {
      this.searchAllHits();
    },

    titleOnly() {
      this.updateSearch();
    },
    fuzzy() {
      this.updateSearch();
    },
    showHighlightsWhileSearchAsYouType() {
      this.updateSearch();
    },
    maxPagesPerHit() {
      this.updateSearch();
    },
    maxHighlightsPerPage() {
      this.updateSearch();
    },
    lastSearchWasAllSearch() {
      this.updateSearch();
    },
    andMode() {
      this.updateSearch();
    },
  },
};
</script>

<!-- Styles cannot be scoped here because of the created html (highlighted search results)-->
<style>
.theme--light .search-area {
  background: #f0f0f0;
}

.theme--dark .search-area {
  background: #151515;
}
.virtual-scroll {
  overflow-y: auto;
}
</style>
